/**
 * Make it a set (Byteex) — <mias-set-picker>
 *
 * Swaps the main product for a Shopify Bundles product chosen on a tile (see snippets/make-it-a-set.liquid).
 *
 * How it plugs into Focal without touching theme.js:
 * - Add to cart: Focal's <form is="product-form"> builds `new FormData(form)`, which fires the native
 *   `formdata` event — we replace `id` with the bundle variant there. The hidden `input[name=id]` is never
 *   mutated, because <product-variants> uses it as its master selector. Sticky ATC submits the same form.
 * - Main variant changes: we listen to Focal's `variant:changed` on the form.
 * - Price: <product-meta>._updatePrices/_updateLabels are called with the bundle variant (guarded — internal API).
 * - Gallery: bundle images are pre-rendered in <product-media> as `.is-filtered` cells (data-mias-set); we toggle
 *   `is-filtered` and reload the carousel. Back to "product only" we re-dispatch `variant:changed` with the current
 *   main variant, so Focal itself restores price, labels, sticky bar and gallery (incl. its alt-# colour filtering).
 */
if (!customElements.get('mias-set-picker')) {
  customElements.define(
    'mias-set-picker',
    class MiasSetPicker extends HTMLElement {
      connectedCallback() {
        const dataElement = this.querySelector('[data-mias-data]');
        this.form = document.getElementById(this.dataset.formId);
        if (!dataElement || !this.form) return;

        try {
          this.data = JSON.parse(dataElement.textContent);
        } catch (error) {
          console.warn('[make-it-a-set] Invalid data JSON', error);
          return;
        }

        const formId = this.dataset.formId;
        this.setsById = new Map(this.data.sets.map((set) => [set.id, set]));
        this.mainVariant = this.data.mainVariants.find((variant) => variant.id === this.data.initialVariantId) || null;
        this.selectedSetId = null;
        this.selectedLabel = this.querySelector('[data-mias-selected-label]');
        this.mainImage = this.querySelector('[data-set-id=""] .mias-tile-image');
        this.productMeta = document.querySelector(`product-meta[form-id="${formId}"]`);
        this.productMedia = document.querySelector(`product-media[form-id="${formId}"]`);
        this.stickyPrice = document.querySelector(`product-sticky-form[form-id="${formId}"] .product-sticky-form__price`);

        this.onTileChange = this.onTileChange.bind(this);
        this.onVariantChanged = this.onVariantChanged.bind(this);
        this.onFormData = this.onFormData.bind(this);

        this.addEventListener('change', this.onTileChange);
        this.form.addEventListener('variant:changed', this.onVariantChanged);
        this.form.addEventListener('formdata', this.onFormData);

        this.refreshTiles();
      }

      disconnectedCallback() {
        this.removeEventListener('change', this.onTileChange);
        if (this.form) {
          this.form.removeEventListener('variant:changed', this.onVariantChanged);
          this.form.removeEventListener('formdata', this.onFormData);
        }
        cancelAnimationFrame(this.galleryFrame);
      }

      // ---------------------------------------------------------------- events

      onTileChange(event) {
        const input = event.target.closest('.mias-tile-input');
        if (!input) return;

        if (input.value) {
          this.selectSet(input.value, input.dataset.label);
        } else {
          this.selectMain();
        }
      }

      onVariantChanged(event) {
        if (event.detail && event.detail.miasRestore) return; // our own restore dispatch

        this.mainVariant = event.detail ? event.detail.variant : null;
        this.updateMainImage();
        this.refreshTiles();

        if (this.selectedSetId) {
          this.applySetPrice();
          // <product-media> may handle this event after us (it subscribes after an async load) → re-apply next frame.
          cancelAnimationFrame(this.galleryFrame);
          this.galleryFrame = requestAnimationFrame(() => this.showSetMedia(this.selectedSetId));
        }
      }

      onFormData(event) {
        const variant = this.selectedSetVariant();
        if (variant) event.formData.set('id', variant.id);
      }

      // ---------------------------------------------------------------- selection

      selectSet(setId, label) {
        if (!this.selectedSetVariant(setId)) return;

        this.selectedSetId = setId;
        this.setSelectedLabel(label);
        this.togglePaymentButton(false);
        this.applySetPrice();
        this.showSetMedia(setId);
      }

      selectMain() {
        const mainInput = this.querySelector('[data-set-id=""] .mias-tile-input');
        if (mainInput) mainInput.checked = true;

        if (!this.selectedSetId) return;
        this.selectedSetId = null;
        this.setSelectedLabel(mainInput ? mainInput.dataset.label : '');
        this.togglePaymentButton(true);
        this.restoreMain();
      }

      setSelectedLabel(label) {
        if (this.selectedLabel) this.selectedLabel.textContent = label;
      }

      // ---------------------------------------------------------------- variants & tiles

      resolveSetVariant(set) {
        if (!set || !this.mainVariant) return null;
        if (set.optionMap.length === 0) return set.variants[0] || null;

        const mainOptions = this.mainVariant.options;
        return (
          set.variants.find((variant) =>
            set.optionMap.every((mainIndex, bundleIndex) => variant.options[bundleIndex] === mainOptions[mainIndex])
          ) || null
        );
      }

      selectedSetVariant(setId = this.selectedSetId) {
        if (!setId) return null;
        const variant = this.resolveSetVariant(this.setsById.get(setId));
        return variant && variant.available && this.mainVariant && this.mainVariant.available ? variant : null;
      }

      refreshTiles() {
        this.querySelectorAll('[data-mias-tile]').forEach((tile) => {
          const setId = tile.dataset.setId;
          if (!setId) return;

          const set = this.setsById.get(setId);
          const variant = this.resolveSetVariant(set);
          const isAvailable = Boolean(variant && variant.available && this.mainVariant && this.mainVariant.available);
          const input = tile.querySelector('.mias-tile-input');
          const badge = tile.querySelector('[data-mias-badge]');
          const hint = tile.querySelector('[data-mias-hint]');

          input.disabled = !isAvailable;
          tile.classList.toggle('is-disabled', !isAvailable);
          if (hint) hint.hidden = isAvailable;

          if (badge) {
            const percent = isAvailable ? this.badgePercent(set, variant) : null;
            badge.hidden = !percent;
            badge.textContent = percent ? `-${percent}%` : '';
          }

          this.updateSetImage(tile, variant);
        });

        if (this.selectedSetId && !this.selectedSetVariant()) this.selectMain();
      }

      badgePercent(set, variant) {
        if (set.badge) return set.badge;
        if (!variant.compare_at_price || variant.compare_at_price <= variant.price) return null;
        // Integer cents → exact division when it divides evenly, so ceil never bumps 35 to 36.
        return Math.ceil(((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price);
      }

      // ---------------------------------------------------------------- tile images

      updateMainImage() {
        const variant = this.mainVariant;
        const media = variant && variant.featured_media;
        if (!this.mainImage || !media || !media.preview_image) return; // no own image → keep the previous one

        this.setImageSource(this.mainImage, media.preview_image.src);
      }

      updateSetImage(tile, variant) {
        const image = tile.querySelector('.mias-tile-image');
        if (!image) return;

        if (!image.dataset.defaultSrc) {
          image.dataset.defaultSrc = image.currentSrc || image.src;
        }

        this.setImageSource(image, variant && variant.image ? variant.image : image.dataset.defaultSrc, true);
      }

      setImageSource(image, src, isSized = false) {
        let url = src;
        if (!isSized) {
          url = `${src}${src.includes('?') ? '&' : '?'}width=240`;
        }
        if (image.getAttribute('src') === url) return;

        image.removeAttribute('srcset');
        image.setAttribute('src', url);
      }

      // ---------------------------------------------------------------- price & buttons

      applySetPrice() {
        const variant = this.selectedSetVariant();
        if (!variant) return;

        if (this.productMeta) {
          if (typeof this.productMeta._updatePrices === 'function') this.productMeta._updatePrices(variant);
          if (typeof this.productMeta._updateLabels === 'function') this.productMeta._updateLabels(variant);
        }

        if (this.stickyPrice) this.stickyPrice.textContent = variant.price_formatted;
      }

      togglePaymentButton(isVisible) {
        this.form.querySelectorAll('.shopify-payment-button').forEach((button) => {
          button.classList.toggle('mias-is-hidden', !isVisible);
        });
      }

      restoreMain() {
        this.hideSetMedia();

        if (this.mainVariant) {
          // Let Focal re-render everything for the current main variant (price, labels, sticky bar, gallery filter).
          this.form.dispatchEvent(
            new CustomEvent('variant:changed', { bubbles: true, detail: { variant: this.mainVariant, miasRestore: true } })
          );
        } else {
          this.showMainMedia();
        }
      }

      // ---------------------------------------------------------------- gallery

      get carousel() {
        return this.productMedia ? this.productMedia.querySelector('flickity-carousel') : null;
      }

      showSetMedia(setId) {
        const carousel = this.carousel;
        if (!carousel) return;

        // Show the matched bundle variant's own image + the bundle's general images; other variants' images stay hidden.
        const variant = this.resolveSetVariant(this.setsById.get(setId));
        const variantMediaId = variant && variant.media_id ? String(variant.media_id) : null;
        const isVisible = (element) =>
          element.dataset.miasSet === setId &&
          (!element.hasAttribute('data-mias-variant-media') || element.dataset.miasMediaId === variantMediaId);

        const setCells = Array.from(carousel.querySelectorAll('[data-mias-set]')).filter(isVisible);
        if (setCells.length === 0) return; // bundle without images → keep the main gallery

        this.productMedia.querySelectorAll('[data-media-id], [data-mias-set]').forEach((element) => {
          element.classList.toggle('is-filtered', !isVisible(element));
        });

        carousel.querySelectorAll('.product__media-item').forEach((cell) => {
          cell.classList.remove('is-selected', 'is-initial-selected');
        });
        // Lowest data-original-position = variant image first (reload() sorts by it)
        const firstCell = setCells.sort(
          (a, b) => parseInt(a.dataset.originalPosition, 10) - parseInt(b.dataset.originalPosition, 10)
        )[0];
        firstCell.classList.add('is-selected', 'is-initial-selected');

        this.productMedia.classList.add('mias-set-active');
        carousel.reload();
      }

      hideSetMedia() {
        if (!this.productMedia) return;
        this.productMedia.querySelectorAll('[data-mias-set]').forEach((element) => {
          element.classList.add('is-filtered');
          element.classList.remove('is-selected', 'is-initial-selected');
        });
        this.productMedia.classList.remove('mias-set-active');
      }

      showMainMedia() {
        const carousel = this.carousel;
        if (!carousel) return;

        this.productMedia.querySelectorAll('[data-media-id]').forEach((element) => element.classList.remove('is-filtered'));
        const firstCell = carousel.querySelector('.product__media-item[data-media-id]');
        if (firstCell) firstCell.classList.add('is-selected', 'is-initial-selected');
        carousel.reload();
      }
    }
  );
}
